import { useState } from "react";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { MAX_UNDO_ENTRIES } from "@design-system/DataGrid/editing/undoStack";

interface UseSpreadsheetHistoryProps {
    onChange: (newData: SpreadsheetData, changedCells: ChangedCells) => void;
    maxHistorySize?: number;
    initialData: SpreadsheetData;
}

interface HistoryItem {
    data: SpreadsheetData;
    changedCells: ChangedCells;
}

export function useSpreadsheetHistory({
    onChange,
    maxHistorySize = MAX_UNDO_ENTRIES,
    initialData,
}: UseSpreadsheetHistoryProps) {
    const [history, setHistory] = useState<HistoryItem[]>([
        { data: initialData, changedCells: [] },
    ]);
    const [undoneChanges, setUndoneChanges] = useState<HistoryItem[]>([]);

    const handleChange = (
        newData: SpreadsheetData,
        changedCells: ChangedCells,
    ) => {
        setHistory((prevHistory) => {
            const newHistory = [
                ...prevHistory,
                { data: newData, changedCells },
            ];
            if (newHistory.length > maxHistorySize) {
                return newHistory.slice(newHistory.length - maxHistorySize);
            }
            return newHistory;
        });
        setUndoneChanges([]);

        onChange(newData, changedCells);
    };

    const handleUndo = () => {
        if (history.length <= 1) {
            return;
        }

        const newHistory = [...history];
        const lastState = newHistory.pop() as HistoryItem;

        setUndoneChanges((prevUndone) => {
            const newUndone = [...prevUndone, lastState];
            if (newUndone.length > maxHistorySize) {
                return newUndone.slice(newUndone.length - maxHistorySize);
            }
            return newUndone;
        });

        setHistory(newHistory);

        const previousState = newHistory[newHistory.length - 1];
        onChange(previousState.data, previousState.changedCells);
    };

    const handleRedo = () => {
        if (undoneChanges.length === 0) {
            return;
        }

        const newUndoneChanges = [...undoneChanges];
        const stateToRedo = newUndoneChanges.pop() as HistoryItem;

        setHistory((prevHistory) => {
            const newHistory = [...prevHistory, stateToRedo];
            if (newHistory.length > maxHistorySize) {
                return newHistory.slice(newHistory.length - maxHistorySize);
            }
            return newHistory;
        });

        setUndoneChanges(newUndoneChanges);
        onChange(stateToRedo.data, stateToRedo.changedCells);
    };

    return { handleChange, handleUndo, handleRedo };
}
